/**
 * Legacy report list URLs (`/r/li/:type/:id`, old `view` + `id` query pairs).
 * Scheduled for removal once external links are migrated.
 */
import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";
import { BUILTIN_REPORT_VIEW_KEYS, ReportListType } from "@web/features/Reports/ReportViewKeys";
import { REPORT_VIEW_SCOPE_QUERY } from "@web/features/Reports/Views/reportViewScopeQuery";

export const LEGACY_REPORT_LIST_TYPE_MAP: Record<string, ReportListType> = {
  aall: ReportListType.TYPE_ALL,
  op: ReportListType.TYPE_OPEN,
  cl: ReportListType.TYPE_CLOSED,
  cbm: ReportListType.TYPE_CREATED_BY_ME,
  gr: ReportListType.TYPE_GUEST,
  tr: ReportListType.TYPE_TRANSFERRED,
  rt: ReportListType.TYPE_REPORT_TOPIC,
  rs: ReportListType.TYPE_REPORT_SORT,
  po: ReportListType.TYPE_PER_PROPERTY,
};

const LEGACY_VIEW_ALIASES: Record<string, string> = {
  "created-by-me": BUILTIN_REPORT_VIEW_KEYS.CREATED_BY_ME,
  "transfer-to-task": BUILTIN_REPORT_VIEW_KEYS.TRANSFERRED,
  "topic": BUILTIN_REPORT_VIEW_KEYS.ALL,
  "sort": BUILTIN_REPORT_VIEW_KEYS.ALL,
  "property": BUILTIN_REPORT_VIEW_KEYS.ALL,
};

export function normalizeLegacyReportViewQuery(view: string): string {
  return LEGACY_VIEW_ALIASES[view] ?? view;
}

export function legacyReportListTypeToQuery(type: ReportListType, id?: string): Record<string, string> {
  switch (type) {
    case ReportListType.TYPE_PER_PROPERTY:
      return id
        ? { view: BUILTIN_REPORT_VIEW_KEYS.ALL, [REPORT_VIEW_SCOPE_QUERY.property]: id }
        : { view: BUILTIN_REPORT_VIEW_KEYS.ALL };
    case ReportListType.TYPE_REPORT_TOPIC:
      return { view: BUILTIN_REPORT_VIEW_KEYS.ALL, [REPORT_VIEW_SCOPE_QUERY.topic]: id ?? "" };
    case ReportListType.TYPE_REPORT_SORT:
      return { view: BUILTIN_REPORT_VIEW_KEYS.ALL, [REPORT_VIEW_SCOPE_QUERY.sort]: id ?? "" };
    case ReportListType.TYPE_CREATED_BY_ME:
      return { view: BUILTIN_REPORT_VIEW_KEYS.CREATED_BY_ME };
    default:
      return { view: normalizeLegacyReportViewQuery(type) };
  }
}

/**
 * Without a view the result has none either: the route then opens the user's default view, and a
 * legacy `id` means nothing without its list type.
 */
export function migrateLegacyReportViewAndIdQuery(query: Record<string, unknown>): Record<string, string> {
  const rawView = typeof query.view === "string" ? query.view : null;
  const id = typeof query.id === "string" ? query.id : undefined;
  const next: Record<string, string> = {};

  for (const [key, value] of Object.entries(query)) {
    if (key === "view" || key === "id") {
      continue;
    }
    if (typeof value === "string") {
      next[key] = value;
    }
  }

  if (rawView === null) {
    return next;
  }
  const view = normalizeLegacyReportViewQuery(rawView);

  if (rawView === ReportListType.TYPE_PER_PROPERTY) {
    Object.assign(next, legacyReportListTypeToQuery(ReportListType.TYPE_PER_PROPERTY, id));
    return next;
  }
  if (rawView === ReportListType.TYPE_REPORT_TOPIC && id) {
    Object.assign(next, legacyReportListTypeToQuery(ReportListType.TYPE_REPORT_TOPIC, id));
    return next;
  }
  if (rawView === ReportListType.TYPE_REPORT_SORT && id) {
    Object.assign(next, legacyReportListTypeToQuery(ReportListType.TYPE_REPORT_SORT, id));
    return next;
  }
  if (rawView === ReportListType.TYPE_CREATED_BY_ME) {
    Object.assign(next, legacyReportListTypeToQuery(ReportListType.TYPE_CREATED_BY_ME));
    return next;
  }

  next.view = view;
  return next;
}

export function isLegacyReportListType(value: unknown): value is ReportListType {
  return typeof value === "string" && (Object.values(ReportListType) as string[]).includes(value);
}

export function legacyReportListRedirect(to: RouteLocationNormalized): RouteLocationRaw {
  const rawType = String(to.params.type ?? "");
  const mapped = LEGACY_REPORT_LIST_TYPE_MAP[rawType] ?? (isLegacyReportListType(rawType) ? rawType : null);
  let id = typeof to.params.id === "string" ? to.params.id : undefined;
  let reportId = typeof to.params.reportId === "string" ? to.params.reportId : undefined;
  if (id && !reportId && (id === "new" || /^r\d+$/.test(id))) {
    reportId = id;
    id = undefined;
  }
  const params: Record<string, string> = {};
  if (reportId === "new") {
    params.reportId = "new";
  } else if (reportId && /^r\d+$/.test(reportId)) {
    params.reportId = reportId.slice(1);
  }

  const queryParts = mapped
    ? legacyReportListTypeToQuery(mapped, id)
    : { view: normalizeLegacyReportViewQuery(rawType) };

  const query: Record<string, string> = {
    ...(to.query as Record<string, string>),
    ...queryParts,
  };
  delete query.id;

  return { name: "report-list", params, query };
}
